/**
 * HitlSideActions — renders gate-declared "side actions" from a HITL question's
 * ``x-actions`` annotation. Shared between ora and the Outlook add-in.
 *
 * A *side action* is a SECONDARY affordance on a HITL gate that triggers a
 * server-side operation WITHOUT resuming the workflow (unlike the approve/reject
 * decision pills in HitlChoicePanel). The pending_edocs gate's "Check Now" is
 * the first user: it polls Epic for the carrier eDoc instead of waiting for the
 * daily sweep.
 *
 * Fully data-driven + transport-agnostic: the component knows nothing about any
 * specific action. The gate declares what's available; the caller's ``onAction``
 * runs the request (Remix fetch in ora, orchestratorApi in the add-in — mirrors
 * HitlChoicePanel's ``onSubmit`` seam); the endpoint owns the behavior AND the
 * human-readable result text (shown verbatim). Example payload:
 *
 *   "x-actions": [{
 *     "id": "check_edocs", "label": "Check now",
 *     "endpoint": "check-edocs", "tooltip": "…", "refresh": true
 *   }]
 */
import { useState } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import CustomTooltip from "~/common/components/CustomTooltip";

/** Result envelope returned by ``onAction`` (the endpoint's JSON response). */
export interface SideActionResult {
  ok?: boolean;
  /** Human-readable success text, owned by the endpoint. Shown verbatim. */
  message?: string;
  /** Human-readable failure text. Shown verbatim in the error tone. */
  error?: string;
}

interface SideAction {
  id: string;
  label: string;
  endpoint: string;
  tooltip?: string;
}

function parseSideActions(
  hitlQuestion: Record<string, unknown> | null | undefined,
): SideAction[] {
  if (!hitlQuestion || typeof hitlQuestion !== "object") return [];
  const raw = (hitlQuestion as Record<string, unknown>)["x-actions"];
  if (!Array.isArray(raw)) return [];
  const out: SideAction[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const o = item as Record<string, unknown>;
    if (
      typeof o.id !== "string" ||
      typeof o.label !== "string" ||
      typeof o.endpoint !== "string"
    ) {
      continue;
    }
    out.push({
      id: o.id,
      label: o.label,
      endpoint: o.endpoint,
      tooltip: typeof o.tooltip === "string" ? o.tooltip : undefined,
    });
  }
  return out;
}

function SideActionButton({
  action,
  onAction,
}: {
  action: SideAction;
  onAction: (endpoint: string) => Promise<SideActionResult>;
}) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<SideActionResult | null>(null);

  const run = async () => {
    setBusy(true);
    setResult(null);
    try {
      setResult(await onAction(action.endpoint));
    } catch (err) {
      setResult({ error: err instanceof Error ? err.message : "Request failed." });
    } finally {
      setBusy(false);
    }
  };

  const button = (
    <button
      type="button"
      onClick={run}
      disabled={busy}
      data-testid={`hitl-side-action-${action.id}`}
      className="h-9 px-4 inline-flex justify-center items-center btn-primary rounded-lg text-sm font-semibold disabled:opacity-40"
    >
      {busy ? <CircularProgress size={14} color="inherit" /> : action.label}
    </button>
  );

  return (
    <div className="flex flex-col gap-1">
      {action.tooltip ? (
        // ora's tooltip, not a native title (story 28633). Node title +
        // describeChild: the button keeps its label as its name.
        <CustomTooltip
          id={`hitl-side-action-${action.id}`}
          title={<span>{action.tooltip}</span>}
          describeChild
          placement="top"
          arrow
          isOnModal
        >
          {button}
        </CustomTooltip>
      ) : (
        button
      )}
      {result?.message && (
        <p
          className="text-xs text-primary-gray-light-700"
          data-testid={`hitl-side-action-msg-${action.id}`}
        >
          {result.message}
        </p>
      )}
      {result?.error && (
        <p
          className="text-xs text-primary-error-700"
          data-testid={`hitl-side-action-err-${action.id}`}
        >
          {result.error}
        </p>
      )}
    </div>
  );
}

export default function HitlSideActions({
  hitlQuestion,
  onAction,
}: {
  hitlQuestion: Record<string, unknown> | null | undefined;
  /** Runs one action by its declared ``endpoint`` and returns the result. */
  onAction: (endpoint: string) => Promise<SideActionResult>;
}) {
  const actions = parseSideActions(hitlQuestion);
  if (actions.length === 0) return null;
  return (
    <div className="mt-2 flex flex-col gap-2" data-testid="hitl-side-actions">
      {actions.map((a) => (
        <SideActionButton key={a.id} action={a} onAction={onAction} />
      ))}
    </div>
  );
}
